/**
 * BrandedLoginHeader
 *
 * Renders the company name with "Powered by Adrsta AI" underneath, starting
 * from the horizontal center of the company name.
 *
 * Colors are pulled from the app's design tokens (src/index.css) so this
 * automatically matches whichever theme (light/dark) is currently active.
 *
 * Props:
 * - companyName   (string)          : Customer's company name. Required.
 * - accentColor   (string)          : Hex/CSS color for the spark icon. Default: var(--violet)
 * - poweredByText (string)          : Override the badge label.
 *
 * Usage:
 * <BrandedLoginHeader companyName="NBCUniversal" accentColor="#1D2D82" />
 */

interface AdrstaSpark {
  size?: number;
  color?: string;
}

function AdrstaSpark({ size = 9, color = "var(--violet)" }: AdrstaSpark) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8 1 L9.2 6.8 L15 8 L9.2 9.2 L8 15 L6.8 9.2 L1 8 L6.8 6.8 Z"
        fill={color}
        opacity="0.85"
      />
      <circle cx="13" cy="3" r="1.1" fill={color} opacity="0.5" />
    </svg>
  );
}

interface BrandedLoginHeaderProps {
  companyName?: string;
  accentColor?: string;
  poweredByText?: string;
}

export default function BrandedLoginHeader({
  companyName = "Your Company",
  accentColor = "var(--violet)",
  poweredByText = "Powered by Adrsta AI",
}: BrandedLoginHeaderProps) {
  return (
    /*
     * `position: relative` + `inline-block` sizes this wrapper to the company
     * name only. The badge is absolutely positioned with its left edge at the
     * horizontal center of that box, so the icon always starts below the
     * middle of the company name — even though this renders inside a
     * <button> whose UA stylesheet defaults to text-align: center.
     */
    <div style={{ display: "inline-block", position: "relative", textAlign: "left" }}>
      <div
        style={{
          fontSize: "1.4rem",
          fontWeight: 800,
          letterSpacing: "-0.03em",
          lineHeight: 1.1,
          color: "var(--text-primary)",
          whiteSpace: "nowrap",
          fontFamily: "var(--font-sans)",
          textAlign: "left",
        }}
      >
        {companyName}
      </div>

      <div
        style={{
          position: "absolute",
          top: "100%",
          left: "50%",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          marginTop: "2px",
          fontSize: "0.55rem",
          fontWeight: 500,
          lineHeight: 1,
          color: "var(--text-tertiary)",
          letterSpacing: "0.015em",
          fontFamily: "var(--font-sans)",
          whiteSpace: "nowrap",
        }}
      >
        <AdrstaSpark size={9} color={accentColor} />
        {poweredByText}
      </div>
    </div>
  );
}
